/**
 * feat-307-redesign-tenant-mgmt (spec D7) — an ⓘ button that discloses help
 * text the page used to print in full under every control.
 *
 * The control the help describes stays on screen; only the paragraph moves.
 *
 * Keyboard: the trigger is a real <button>, so Enter and Space open it natively.
 * Escape closes it from anywhere and returns focus to the trigger. A click
 * outside closes it too.
 *
 * The panel stays MOUNTED while closed (the `hidden` attribute, not a
 * conditional render). That keeps an existing `aria-describedby` pointing at the
 * help paragraph valid (TenantDripControl's rate input relies on it), and keeps
 * the verbatim copy in the DOM, where the copy-lock tests read it.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Info } from './icons';

export interface InfoPopoverProps {
  /** Accessible name of the trigger, e.g. "What does Live client do?". */
  label: string;
  children: React.ReactNode;
  /** Optional stable id for the panel (otherwise generated). */
  panelId?: string;
  /** data-testid prefix: `${testId}-trigger` / `${testId}-panel`. */
  testId?: string;
  /** Which edge the panel aligns to. */
  align?: 'left' | 'right';
}

export default function InfoPopover({ label, children, panelId, testId, align = 'left' }: InfoPopoverProps) {
  const generated = useId();
  const id = panelId ?? `info-popover-${generated.replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-flex align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        data-testid={testId ? `${testId}-trigger` : undefined}
        onClick={() => setOpen((o) => !o)}
        className="text-inkMuted hover:text-ink p-0.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
      >
        <Info className="w-4 h-4" aria-hidden="true" />
      </button>
      <div
        id={id}
        role="note"
        hidden={!open}
        data-testid={testId ? `${testId}-panel` : undefined}
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-7 z-30 w-[26rem] max-w-[80vw] bg-white border border-softBorder rounded-xl shadow-lg p-3 text-xs text-ink leading-relaxed font-normal normal-case tracking-normal text-left`}
      >
        {children}
      </div>
    </div>
  );
}
